import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { VideoLogo } from '@/store/videoFeatures';
import { buildLogoUrl } from '@/components/feature-configs/logo/logo-utils';
import { cn } from '@/lib/utils';

interface LogoOverlayProps {
  logo: VideoLogo;
}

export const LogoOverlay: React.FC<LogoOverlayProps> = ({ logo }) => {
  useLanguage();
  // Get base position based on 9-point grid
  const getBasePosition = (): React.CSSProperties => {
    switch (logo.position) {
      case 'top-left':
        return { top: '10px', left: '10px' };
      case 'top-center':
        return { top: '10px', left: '50%', transform: 'translateX(-50%)' };
      case 'top-right':
        return { top: '10px', right: '10px' };
      case 'center-left':
        return { top: '50%', left: '10px', transform: 'translateY(-50%)' };
      case 'center':
        return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
      case 'center-right':
        return { top: '50%', right: '10px', transform: 'translateY(-50%)' };
      case 'bottom-left':
        return { bottom: '10px', left: '10px' };
      case 'bottom-center':
        return { bottom: '10px', left: '50%', transform: 'translateX(-50%)' };
      case 'bottom-right':
        return { bottom: '10px', right: '10px' };
      default:
        return { top: '10px', left: '10px' };
    }
  };

  // Apply offsets if they exist
  const getPositionStyle = (): React.CSSProperties => {
    const basePos = getBasePosition();
    
    if (logo.offsetX || logo.offsetY) {
      const offsetX = logo.offsetX || 0;
      const offsetY = logo.offsetY || 0;
      
      // Combine transforms if base position has transform
      if (basePos.transform) {
        return {
          ...basePos,
          transform: `${basePos.transform} translate(${offsetX}px, ${offsetY}px)`,
        };
      }
      
      return {
        ...basePos,
        transform: `translate(${offsetX}px, ${offsetY}px)`,
      };
    }
    
    return basePos;
  };

  // Build complete styles including customization
  const getLogoStyles = (): React.CSSProperties => {
    const styles: React.CSSProperties = {
      ...getPositionStyle(),
      position: 'absolute',
      maxWidth: `${logo.size || 10}%`,
      maxHeight: `${logo.size || 10}%`,
      opacity: logo.opacity ?? 1,
      zIndex: 10,
      transition: 'all 0.3s ease',
    };

    // Apply border if enabled
    if (logo.customization?.border?.enabled) {
      styles.border = `${logo.customization.border.width}px solid ${logo.customization.border.color}`;
      styles.borderRadius = '4px';
      styles.overflow = 'hidden';
    }

    // Apply shadow if enabled
    if (logo.customization?.shadow?.enabled) {
      const { color, blur, intensity } = logo.customization.shadow;
      // Combine box-shadow and filter for best effect
      styles.filter = `drop-shadow(0 2px ${blur}px ${color}) brightness(${100 + (intensity - 50)}%)`;
    }

    return styles;
  };

  // Get hover effect class
  const getHoverClass = (): string => {
    const effect = logo.customization?.hoverEffect;
    if (!effect || effect === 'none') return '';

    switch (effect) {
      case 'darken':
        return 'hover:brightness-95';
      case 'lighten':
        return 'hover:brightness-105';
      case 'scale':
        return 'hover:scale-105';
      default:
        return '';
    }
  };

  const imageUrl = logo.imageUrl || logo.url;
  const linkUrl = buildLogoUrl(logo);

  // Don't render if no image
  if (!imageUrl) return null;

  const logoImage = (
    <img
      src={imageUrl}
      alt={translateInterfaceText("Logo")}
      className="w-full h-auto object-contain"
    />
  );

  return (
    <div
      style={getLogoStyles()}
      className={cn('transition-all duration-300', getHoverClass())}
    >
      {linkUrl ? (
        <a
          href={linkUrl}
          target={logo.linkTarget === 'same' ? '_self' : '_blank'}
          rel="noopener noreferrer"
          className="block"
        >
          {logoImage}
        </a>
      ) : (
        logoImage
      )}
    </div>
  );
};
